import type {Caption} from '@remotion/captions';
import type {EditorState} from '../../vendor/src/features/remotion-editor/editor/state/types';
import {splitItem} from '../../vendor/src/features/remotion-editor/editor/state/actions/split-item';
import {withoutCaptionTranslation} from '../../vendor/src/features/remotion-editor/editor/state/actions/caption-translation-actions';
import {calculateCaptionAssetSize} from '../../vendor/src/features/remotion-editor/editor/editor-property-panel/utils/caption/asset-utils';
import {hasPendingTimelineEdit} from './edit-ownership';
import {findCaptionPanelTrackOwner} from './caption-panel-tracks';
import {projectCaptionViewWindow} from './caption-view-window';

export type CaptionCaretSplitInput = {itemId:string;wordIndex:number;offset:number;displayText:string};
export type CaptionCaretSplitResult = {ok:true;state:EditorState;firstId:string;secondId:string}|{ok:false;reason:string};

/** DOM offsets are UTF-16; a caret must fall on a whole grapheme boundary. */
function fractionAt(text:string,offset:number):number|null {
 const pieces=[...new Intl.Segmenter(undefined,{granularity:'grapheme'}).segment(text)];
 let consumed=0;
 for(let i=0;i<=pieces.length;i++){
  if(consumed===offset)return pieces.length?i/pieces.length:null;
  consumed+=pieces[i]?.segment.length??0;
 }
 return null;
}

export function planCaptionCaretSplit(state:EditorState,input:CaptionCaretSplitInput):CaptionCaretSplitResult {
 const fail=(reason:string):CaptionCaretSplitResult=>({ok:false,reason});
 const d=state.undoableState,item=d.items[input.itemId],owner=findCaptionPanelTrackOwner(d.tracks,input.itemId,d.items);
 if(item?.type!=='captions'||!owner||owner.track.locked||item.groupId||hasPendingTimelineEdit(state))return fail('잠금을 풀고 진행 중인 편집을 마친 뒤 다시 나눠 주세요.');
 if(d.tracks.filter(track=>track.items.includes(item.id)).length!==1)return fail('자막 트랙 연결을 확인해 주세요.');
 const asset=d.assets[item.assetId];
 if(asset?.type!=='caption'||!Number.isInteger(input.wordIndex)||!Number.isInteger(input.offset))return fail('자막을 다시 선택해 주세요.');
 if(Object.values(d.items).some(i=>i.type==='captions'&&(i.id===item.id&&i.captionPresentationNextItemId||i.captionPresentationNextItemId===item.id)))return fail('병합한 자막은 먼저 분리한 뒤 Enter로 나눠 주세요.');
 if(!Number.isFinite(d.fps)||d.fps<=0||item.durationInFrames<2)return fail('나눌 수 있는 자막 구간이 없습니다.');
 const sourceStartMs=item.captionStartInSeconds*1000;
 const sourceEndMs=sourceStartMs+item.durationInFrames/d.fps*1000;
 if(!Number.isFinite(sourceStartMs)||!Number.isFinite(sourceEndMs))return fail('자막 시간을 확인한 뒤 나눠 주세요.');
 // The panel passes the source-array index, even for a clipped scene-voice
 // view. Split only the words actually visible in this item's source window.
 const entries=asset.timelineSourceAssetId
  ? projectCaptionViewWindow(asset.captions,{timelineSourceAssetId:asset.timelineSourceAssetId,
      captionStartInSeconds:item.captionStartInSeconds,durationInFrames:item.durationInFrames,
      fps:d.fps,captionEdgeHold:item.captionEdgeHold})
    .filter(({caption})=>caption.endMs>sourceStartMs&&caption.startMs<sourceEndMs)
  : asset.captions.map((caption,index)=>({index,caption}));
 const visibleIndex=entries.findIndex(entry=>entry.index===input.wordIndex);
 const word=entries[visibleIndex]?.caption;
 if(!word)return fail('나눌 수 있는 자막 구간이 없습니다.');
 const leading=word.text.match(/^\s*/)?.[0]??'',display=word.text.slice(leading.length).replace(/(?:\r?\n)\s*$/,'');
 if(display!==input.displayText||input.offset<0||input.offset>display.length)return fail('입력 내용이 바뀌었습니다. 커서를 다시 놓고 Enter를 눌러 주세요.');
 const graphemeFraction=fractionAt(display,input.offset);
 if(graphemeFraction===null)return fail('글자 사이에 커서를 놓고 나눠 주세요.');
 const fraction=!display.slice(0,input.offset).trim()?0:!display.slice(input.offset).trim()?1:graphemeFraction;
 const rawOffset=leading.length+input.offset;
 const leftText=word.text.slice(0,rawOffset),rightText=word.text.slice(rawOffset);
 const left:Caption[]=entries.slice(0,visibleIndex).map(({caption})=>({...caption}));
 const right:Caption[]=entries.slice(visibleIndex+1).map(({caption})=>({...caption}));
 let cutMs:number;
 if(fraction===0){right.unshift({...word});cutMs=word.startMs;}
 else if(fraction===1){left.push({...word});cutMs=right[0]?.startMs??word.endMs;}
 else {
  if(!leftText.trim()||!rightText.trim())return fail('빈 자막이 생기지 않도록 글자 사이에서 나눠 주세요.');
  cutMs=word.startMs+(word.endMs-word.startMs)*fraction;
  left.push({...word,text:leftText,endMs:cutMs,timestampMs:word.startMs});
  right.unshift({...word,text:rightText,startMs:cutMs,timestampMs:cutMs});
 }
 if(!left.length||!right.length)return fail('자막의 처음이나 끝에서는 나눌 수 없습니다.');
 if(!Number.isFinite(sourceStartMs)||!Number.isFinite(cutMs)||!Number.isFinite(word.startMs)||!Number.isFinite(word.endMs)||word.endMs<=word.startMs)return fail('자막 시간을 확인한 뒤 나눠 주세요.');
 const frame=item.from+Math.round((cutMs-sourceStartMs)*d.fps/1000);
 if(frame<=item.from||frame>=item.from+item.durationInFrames)return fail('양쪽 자막이 한 프레임 이상 남는 위치에서 나눠 주세요.');
 const splitMs=(item.captionStartInSeconds+(frame-item.from)/d.fps)*1000;
 // Align the token split to the frame boundary; trim timing overlaps to each side below.
 if(fraction>0&&fraction<1){left[left.length-1]={...left[left.length-1]!,endMs:splitMs};right[0]={...right[0]!,startMs:splitMs,timestampMs:splitMs};}
 if(left.some(c=>!Number.isFinite(c.startMs)||!Number.isFinite(c.endMs)||c.endMs<=c.startMs||c.startMs>=splitMs)||right.some(c=>!Number.isFinite(c.startMs)||!Number.isFinite(c.endMs)||c.endMs<=c.startMs||c.endMs<=splitMs))return fail('겹치는 자막 시간을 먼저 조정해 주세요.');
 for(let i=0;i<left.length;i++)left[i]={...left[i]!,endMs:Math.min(left[i]!.endMs,splitMs)};
 for(let i=0;i<right.length;i++)right[i]={...right[i]!,startMs:Math.max(right[i]!.startMs,splitMs)};
 const split=splitItem({state,idToSplit:item.id,framePosition:frame});
 if(split===state)return fail('자막을 나누지 못했습니다. 선택과 잠금을 확인해 주세요.');
 const ids=split.undoableState.tracks[owner.trackIndex]!.items.slice(owner.itemIndex,owner.itemIndex+2);
 const first=split.undoableState.items[ids[0]!],second=split.undoableState.items[ids[1]!];
 if(first?.type!=='captions'||second?.type!=='captions')return fail('자막을 나누지 못했습니다.');
 const assets={...split.undoableState.assets};
 for(const [piece,captions] of [[first,left],[second,right]] as const){
  const copied=assets[piece.assetId];if(copied?.type!=='caption')return fail('자막 원본을 나누지 못했습니다.');
  assets[piece.assetId]={...withoutCaptionTranslation(copied),captions,size:calculateCaptionAssetSize(captions),videoStart:piece.captionStartInSeconds*1000,videoEnd:(piece.captionStartInSeconds+piece.durationInFrames/d.fps)*1000};
 }
 // The page renderer rounds milliseconds. A newly cut token begins exactly at
 // this clip edge, so keep it visible on frame zero rather than rounding up.
 const splitItems=fraction>0&&fraction<1?{...split.undoableState.items,[second.id]:{...second,captionEdgeHold:{...second.captionEdgeHold,left:true}}}:split.undoableState.items;
 return {ok:true,firstId:first.id,secondId:second.id,state:{...split,undoableState:{...split.undoableState,items:splitItems,assets},timelineTimeEditApplied:true}};
}

export function readCaptionCaret(element:HTMLElement):{offset:number;displayText:string}|null {
 const selection=element.ownerDocument.defaultView?.getSelection();
 if(!selection?.isCollapsed||!selection.rangeCount)return null;
 const range=selection.getRangeAt(0);
 if(!element.contains(range.startContainer))return null;
 const before=range.cloneRange();before.selectNodeContents(element);before.setEnd(range.startContainer,range.startOffset);
 return {offset:before.toString().length,displayText:element.textContent??''};
}
